<dbw-investor-viewer-accordion
  anchorId="investor-viewer-personnes-physiques"
  accordionTitle="Personnes physiques"
  [itemsCount]="summaries().length"
  [loading]="loading()"
  [expanded]="true">
  @if (canExecutePersonsCorrectionAction() && summaries().length > 1) {
    <oui-button
      accordion-actions
      type="outlined"
      iconSuffix="build"
      (click)="openCorrectionDialog()"
      [disabled]="loading() || persons().length !== summaries().length">
      Corriger
    </oui-button>
  }

  @if (loading()) {
    <p>{{ loadedPersonCount }} / {{ summaries().length }} personne(s) chargée(s).</p>
  }
  @if (error()) {
    <p>{{ error() }}</p>
  }

  @if (!loading() && persons().length === 0) {
    <div class="empty-state">Aucune personne physique détaillée disponible.</div>
  } @else {
    <div class="person-list">
      @for (person of persons(); track person.personId) {
        <dbw-investor-viewer-accordion
          [accordionTitle]="formatPersonLabel(person)"
          [itemsCount]="getPersonRelationCount(person)"
          level="subtitle">
          <div class="note-banner dbw-card dbw-card--md">
            <span class="identity-item__label">Identifiants personne</span>
            <strong>
              PersonId {{ person.personId }} • Code {{ formatText(person.personCode) }} • ArianeId
              {{ formatText(person.arianeId) }}
            </strong>
          </div>

          <div class="section-stack">
            <dbw-investor-viewer-accordion
              accordionTitle="Identité"
              level="subtitle">
              <span accordion-description>{{ getIdentitySummary(person) }}</span>
              <div class="detail-grid">
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Civilité</span><strong>{{ formatText(person.civilityCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Nom</span><strong>{{ formatText(person.lastName) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Prénom</span><strong>{{ formatText(person.firstName) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Deuxième prénom</span><strong>{{ formatText(person.middleName) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Troisième prénom</span><strong>{{ formatText(person.thirdName) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Nom de naissance</span><strong>{{ formatText(person.birthName) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Date de naissance</span><strong>{{ formatDate(person.birthDate) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Ville de naissance</span><strong>{{ formatText(person.birthCity) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Département de naissance</span><strong>{{ formatText(person.birthDepartment) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Pays de naissance</span><strong>{{ formatText(person.birthCountryCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Nationalité</span><strong>{{ formatText(person.nationalityCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Nationalité secondaire</span
                  ><strong>{{ formatText(person.secondaryNationalityCode) }}</strong>
                </article>
              </div>
            </dbw-investor-viewer-accordion>

            <dbw-investor-viewer-accordion
              accordionTitle="Protection et état civil"
              level="subtitle">
              <span accordion-description>{{ getCivilStatusSummary(person) }}</span>
              <div class="detail-grid">
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Mesure de protection</span
                  ><strong>{{ formatText(person.protectionMeasureCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Date mesure de protection</span
                  ><strong>{{ formatDate(person.protectionMeasureDate) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Situation</span><strong>{{ formatText(person.maritalStatusCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Changement de situation</span
                  ><strong>{{ formatDate(person.maritalStatusChangeDate) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Date contrat union</span><strong>{{ formatDate(person.unionContractDate) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Régime matrimonial</span><strong>{{ formatText(person.matrimonialRegimeCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Résidence stable du couple</span
                  ><strong>{{ formatText(person.stableCoupleResidenceCountryCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Avantage matrimonial</span
                  ><strong>{{ formatText(person.matrimonialAdvantageCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">PACS indivision</span><strong>{{ formatBoolean(person.isPacsIndivision) }}</strong>
                </article>
              </div>
            </dbw-investor-viewer-accordion>

            <dbw-investor-viewer-accordion
              accordionTitle="Dispositions"
              level="subtitle">
              <span accordion-description>{{ getDispositionSummary(person) }}</span>
              <div class="detail-grid">
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Donation dernier vivant</span
                  ><strong>{{ formatBoolean(person.hasDonationDernierVivant) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Donation conjoint</span><strong>{{ formatBoolean(person.hasDonationConjoint) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Donation partage</span><strong>{{ formatBoolean(person.hasDonationPartage) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Autres donations</span><strong>{{ formatBoolean(person.hasOtherDonations) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Legs particuliers</span><strong>{{ formatBoolean(person.hasLegsParticuliers) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Legs universels</span><strong>{{ formatBoolean(person.hasLegsUniversels) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Autres dispositions</span
                  ><strong>{{ formatBoolean(person.hasOtherDispositions) }}</strong>
                </article>
              </div>

              @if (person.dispositionNotes.length > 0) {
                <div class="note-banner dbw-card dbw-card--md">
                  <span class="identity-item__label">Notes de disposition</span>
                  <strong>{{ formatNotes(person.dispositionNotes) }}</strong>
                </div>
              }
            </dbw-investor-viewer-accordion>

            <dbw-investor-viewer-accordion
              accordionTitle="Situation professionnelle"
              level="subtitle">
              <span accordion-description>{{ getProfessionalSummary(person) }}</span>
              <div class="detail-grid">
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Catégorie socio-professionnelle</span
                  ><strong>{{ formatText(person.socioProfessionalCategoryCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Profession</span><strong>{{ formatText(person.profession) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Société</span><strong>{{ formatText(person.companyName) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Ancienneté</span><strong>{{ formatText(person.seniority) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Origine des revenus</span><strong>{{ formatText(person.revenueOrigin) }}</strong>
                </article>
              </div>

              @if (person.professionalSituationNotes.length > 0) {
                <div class="note-banner dbw-card dbw-card--md">
                  <span class="identity-item__label">Notes professionnelles</span>
                  <strong>{{ formatNotes(person.professionalSituationNotes) }}</strong>
                </div>
              }
            </dbw-investor-viewer-accordion>

            <dbw-investor-viewer-accordion
              accordionTitle="Coordonnées"
              level="subtitle"
              [itemsCount]="getPersonContactCount(person)">
              <span accordion-description>{{ getContactSummary(person) }}</span>

              <div class="mini-block">
                <div class="subheading subheading--small">
                  <h5>Adresses</h5>
                </div>
                @if (person.addresses.length === 0) {
                  <div class="empty-inline">Aucune adresse.</div>
                } @else {
                  <div class="mini-card-list">
                    @for (address of person.addresses; track address.addressId) {
                      <article class="mini-card dbw-card dbw-card--lg">
                        <span class="detail-item__label">{{ formatText(address.addressTypeCode) }}</span>
                        <strong>{{ formatAddress(address) }}</strong>
                        <span>{{ formatText(address.countryCode) }} • Logement {{ formatText(address.housingModeCode) }}</span>
                        @if (address.notes?.trim()) {
                          <span>{{ address.notes }}</span>
                        }
                      </article>
                    }
                  </div>
                }
              </div>

              <div class="mini-block">
                <div class="subheading subheading--small">
                  <h5>Téléphones</h5>
                </div>
                @if (person.phones.length === 0) {
                  <div class="empty-inline">Aucun téléphone.</div>
                } @else {
                  <div class="mini-card-list">
                    @for (phone of person.phones; track phone.phoneId) {
                      <article class="mini-card dbw-card dbw-card--lg">
                        <span class="detail-item__label">{{ formatText(phone.phoneTypeCode) }}</span>
                        <strong>{{ formatPhone(phone) }}</strong>
                        @if (phone.notes?.trim()) {
                          <span>{{ phone.notes }}</span>
                        }
                      </article>
                    }
                  </div>
                }
              </div>

              <div class="mini-block">
                <div class="subheading subheading--small"><h5>Emails</h5></div>
                @if (person.emails.length === 0) {
                  <div class="empty-inline">Aucun email.</div>
                } @else {
                  <div class="mini-card-list">
                    @for (email of person.emails; track email.emailId) {
                      <article class="mini-card dbw-card dbw-card--lg">
                        <span class="detail-item__label">{{ formatText(email.emailTypeCode) }}</span>
                        <strong>{{ formatText(email.address) }}</strong>
                        @if (email.notes?.trim()) {
                          <span>{{ email.notes }}</span>
                        }
                      </article>
                    }
                  </div>
                }
              </div>
            </dbw-investor-viewer-accordion>

            <dbw-investor-viewer-accordion
              accordionTitle="Réglementaire"
              level="subtitle">
              <div
                accordion-description
                class="tag-cluster">
                <oui-tag
                  type="status"
                  size="big"
                  [status]="getRegulatoryTagStatus(person.isPoliticallyExposed, 'error')">
                  {{ getRegulatoryTagLabel(person.isPoliticallyExposed, 'PPE', 'Non PPE') }}
                </oui-tag>
                <oui-tag
                  type="status"
                  size="big"
                  [status]="getRegulatoryTagStatus(person.isUsCitizenOrResident, 'pending')">
                  {{ getRegulatoryTagLabel(person.isUsCitizenOrResident, 'US person', 'Non US person') }}
                </oui-tag>
              </div>
              <div class="detail-grid">
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Pays FATCA</span><strong>{{ formatText(person.fatcaResidenceCountryCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Citoyen / résident US</span
                  ><strong>{{ formatBoolean(person.isUsCitizenOrResident) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">TIN</span><strong>{{ formatText(person.tinCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">NIF</span><strong>{{ formatText(person.nifCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">PPE</span><strong>{{ formatBoolean(person.isPoliticallyExposed) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Relation PPE</span
                  ><strong>{{ formatText(person.politicallyExposedRelationCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Motif PPE</span><strong>{{ formatText(person.politicallyExposedReasonCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Pays PPE</span><strong>{{ formatText(person.politicallyExposedCountryCode) }}</strong>
                </article>
                <article class="detail-item dbw-card dbw-card--lg">
                  <span class="detail-item__label">Ancienneté PPE</span
                  ><strong>{{ formatText(person.politicallyExposedSeniority) }}</strong>
                </article>
              </div>
            </dbw-investor-viewer-accordion>

            <dbw-investor-viewer-accordion
              accordionTitle="Relations"
              level="subtitle"
              [itemsCount]="getPersonRelationCount(person)"
              [expanded]="true">
              <span accordion-description>{{ getRelationsSummary(person) }}</span>

              <div class="mini-block">
                <div class="subheading subheading--small"><h5>Couple</h5></div>
                @if (person.coupleRelations.length === 0) {
                  <div class="empty-inline">Aucune relation de couple.</div>
                } @else {
                  <div class="mini-card-list">
                    @for (relation of person.coupleRelations; track $index) {
                      <article class="mini-card dbw-card dbw-card--lg relation-card">
                        <div class="relation-card__header">
                          <strong>{{ formatCoupleRelation(relation) }}</strong>
                          <div class="relation-card__actions">
                            @if (hasCrossConsultantWarning(relation)) {
                              <oui-tag
                                type="status"
                                size="big"
                                status="error">
                                Autre consultant
                              </oui-tag>
                            }
                            @if (canExecuteRelationDeletionAction() && hasRelatedInvestor(relation) && relation.relatedPersonId) {
                              <oui-button
                                type="icon"
                                [disabled]="loading()"
                                (click)="openCoupleRelationDeletionDialog(person, relation)">
                                <oui-icon>delete</oui-icon>
                              </oui-button>
                            }
                          </div>
                        </div>
                        @if (shouldShowRelatedPersonLabel(relation)) {
                          <div class="relation-card__person">
                            {{ getRelatedPersonLabel(relation) }}
                          </div>
                        }
                        @if (hasRelatedInvestor(relation)) {
                          <dbw-investor-card
                            [id]="relation.relatedInvestorId"
                            [code]="getRelatedInvestorCode(relation)"
                            [name]="getRelatedInvestorName(relation)"
                            [statusCode]="relation.relatedInvestorStatusCode">
                          </dbw-investor-card>
                        }
                        @if (hasCrossConsultantWarning(relation)) {
                          <dbw-consultant-card
                            [id]="relation.relatedConsultantId"
                            [code]="getRelatedConsultantCode(relation)"
                            [name]="getRelatedConsultantName(relation)"
                            [statusCode]="relation.relatedConsultantStatusCode">
                          </dbw-consultant-card>
                        }
                        @if (getRelationReferenceItems(relation).length > 0) {
                          <div class="field-chip-list">
                            @for (item of getRelationReferenceItems(relation); track item) {
                              <span class="field-chip">{{ item }}</span>
                            }
                          </div>
                        }
                      </article>
                    }
                  </div>
                }
              </div>

              <div class="mini-block">
                <div class="subheading subheading--small"><h5>Famille</h5></div>
                @if (person.familyRelations.length === 0) {
                  <div class="empty-inline">Aucune relation familiale.</div>
                } @else {
                  <div class="mini-card-list">
                    @for (relation of person.familyRelations; track relation.familyRelationId) {
                      <article class="mini-card dbw-card dbw-card--lg relation-card">
                        <div class="relation-card__header">
                          <strong>{{ formatFamilyRelation(relation) }}</strong>
                          <div class="relation-card__actions">
                            @if (hasCrossConsultantWarning(relation)) {
                                <oui-tag
                                  type="status"
                                  size="big"
                                  status="error">
                                  Autre consultant
                                </oui-tag>
                            }
                            @if (canExecuteRelationDeletionAction()) {
                              <oui-button
                                type="icon"
                                [disabled]="loading()"
                                (click)="openRelationDeletionDialog(person, relation, 'famille')">
                                <oui-icon>delete</oui-icon>
                              </oui-button>
                            }
                          </div>
                        </div>
                        @if (shouldShowRelatedPersonLabel(relation)) {
                          <div class="relation-card__person">
                            {{ getRelatedPersonLabel(relation) }}
                          </div>
                        }
                        @if (hasRelatedInvestor(relation)) {
                          <dbw-investor-card
                            [id]="relation.relatedInvestorId"
                            [code]="getRelatedInvestorCode(relation)"
                            [name]="getRelatedInvestorName(relation)"
                            [statusCode]="relation.relatedInvestorStatusCode">
                          </dbw-investor-card>
                        }
                        @if (hasCrossConsultantWarning(relation)) {
                          <dbw-consultant-card
                            [id]="relation.relatedConsultantId"
                            [code]="getRelatedConsultantCode(relation)"
                            [name]="getRelatedConsultantName(relation)"
                            [statusCode]="relation.relatedConsultantStatusCode">
                          </dbw-consultant-card>
                        }
                        @if (getFamilyBadges(relation).length > 0 || getRelationReferenceItems(relation).length > 0) {
                          <div class="field-chip-list">
                            @for (item of getFamilyBadges(relation); track item) {
                              <span class="field-chip">{{ item }}</span>
                            }
                            @for (item of getRelationReferenceItems(relation); track item) {
                              <span class="field-chip">{{ item }}</span>
                            }
                          </div>
                        }
                      </article>
                    }
                  </div>
                }
              </div>

              <div class="mini-block">
                <div class="subheading subheading--small"><h5>Autres</h5></div>
                @if (person.otherRelations.length === 0) {
                  <div class="empty-inline">Aucune autre relation.</div>
                } @else {
                  <div class="mini-card-list">
                    @for (relation of person.otherRelations; track relation.otherRelationId) {
                      <article class="mini-card dbw-card dbw-card--lg relation-card">
                        <div class="relation-card__header">
                          <strong>{{ formatOtherRelation(relation) }}</strong>
                          <div class="relation-card__actions">
                            @if (hasCrossConsultantWarning(relation)) {
                                <oui-tag
                                  type="status"
                                  size="big"
                                  status="error">
                                  Autre consultant
                                </oui-tag>
                            }
                            @if (canExecuteRelationDeletionAction()) {
                              <oui-button
                                type="icon"
                                [disabled]="loading()"
                                (click)="openRelationDeletionDialog(person, relation, 'autre')">
                                <oui-icon>delete</oui-icon>
                              </oui-button>
                            }
                          </div>
                        </div>
                        @if (shouldShowRelatedPersonLabel(relation)) {
                          <div class="relation-card__person">
                            {{ getRelatedPersonLabel(relation) }}
                          </div>
                        }
                        @if (hasRelatedInvestor(relation)) {
                          <dbw-investor-card
                            [id]="relation.relatedInvestorId"
                            [code]="getRelatedInvestorCode(relation)"
                            [name]="getRelatedInvestorName(relation)"
                            [statusCode]="relation.relatedInvestorStatusCode">
                          </dbw-investor-card>
                        }
                        @if (hasCrossConsultantWarning(relation)) {
                          <dbw-consultant-card
                            [id]="relation.relatedConsultantId"
                            [code]="getRelatedConsultantCode(relation)"
                            [name]="getRelatedConsultantName(relation)"
                            [statusCode]="relation.relatedConsultantStatusCode">
                          </dbw-consultant-card>
                        }
                        @if (getRelationReferenceItems(relation).length > 0) {
                          <div class="field-chip-list">
                            @for (item of getRelationReferenceItems(relation); track item) {
                              <span class="field-chip">{{ item }}</span>
                            }
                          </div>
                        }
                      </article>
                    }
                  </div>
                }
              </div>
            </dbw-investor-viewer-accordion>
          </div>
        </dbw-investor-viewer-accordion>
      }
    </div>
  }
</dbw-investor-viewer-accordion>
